import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutoAwesome as GenerateIcon } from '@mui/icons-material';
import { DraftCase, DraftInput, GenerateResult, InputPreview, MaterialKind, TargetInfo, TestsApi, fmtCredits } from '../../services/testsApi';
import { InputChips, expectedSummary, modelLabel } from './common';
import { errorText } from './dialogs';

const COUNTS = [3, 5, 10];

const preview = (inputs: DraftInput[]): InputPreview[] =>
  inputs.map(i =>
    i.media
      ? { field: i.field, type: (i.media.kind as MaterialKind) || 'file', text: i.media.description || i.media.prompt || '' }
      : i.json !== undefined
        ? { field: i.field, type: 'json', text: JSON.stringify(i.json) }
        : { field: i.field, type: 'text', text: i.text || '' },
  );

/** Draft test cases for a function or chain with the case generator, pick the good ones, save them.
 * Billed to the account being tested. */
export const GenerateCasesDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  target: TargetInfo;
  existingCount: number;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({ open, api, target, existingCount, isAdmin, onClose, onSaved }) => {
  const [count, setCount] = useState(5);
  const [guidance, setGuidance] = useState('');
  const [avoidExisting, setAvoidExisting] = useState(true);
  const [images, setImages] = useState(true);
  const [drafts, setDrafts] = useState<DraftCase[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [last, setLast] = useState<GenerateResult | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [credits, setCredits] = useState(0);
  const [busy, setBusy] = useState<'generating' | 'saving' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const takesImages = target.target_type === 'chain' || (target.input_types || []).includes('image');

  useEffect(() => {
    if (!open) return;
    setDrafts([]);
    setSelected(new Set());
    setLast(null);
    setProblems([]);
    setCredits(0);
    setError(null);
    setGuidance('');
    if (!isAdmin) return;
    api
      .settings()
      .then(s => setCount(s.generator_count || 5))
      .catch(() => undefined);
  }, [api, open, isAdmin]);

  const generate = async () => {
    setBusy('generating');
    setError(null);
    try {
      const r = await api.generateCases({
        target_type: target.target_type,
        target_id: target.target_id,
        count,
        guidance: guidance.trim() || undefined,
        avoid_existing: avoidExisting,
        images: takesImages && images,
        avoid: drafts,
      });
      const start = drafts.length;
      setDrafts(d => [...d, ...r.cases]);
      setSelected(s => new Set([...Array.from(s), ...r.cases.map((_, i) => start + i)]));
      setLast(r);
      setProblems(r.problems);
      setCredits(c => c + r.credits);
      if (!r.cases.length) setError('No usable cases came back. Try again, or add some guidance.');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    const chosen = drafts.filter((_, i) => selected.has(i));
    if (!chosen.length) return;
    setBusy('saving');
    setError(null);
    try {
      const r = await api.saveGeneratedCases({ target_type: target.target_type, target_id: target.target_id, cases: chosen, images: takesImages && images });
      const parts = [`Saved ${r.saved} test case${r.saved === 1 ? '' : 's'}`];
      if (r.images) parts.push(`made ${r.images} image${r.images === 1 ? '' : 's'}`);
      if (r.switched_off) parts.push(`${r.switched_off} switched off until you attach a file (edit the case)`);
      const total = credits + r.credits;
      onSaved(`${parts.join(', ')}. ${fmtCredits(total)} credits.${r.problems.length ? ` ${r.problems.join('; ')}` : ''}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const toggle = (i: number) =>
    setSelected(s => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const chosen = drafts.filter((_, i) => selected.has(i));
  const imagesToMake = takesImages && images ? chosen.reduce((n, d) => n + d.images, 0) : 0;
  const gen = last?.generator;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate test cases for {target.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The case generator reads this {target.target_type === 'chain' ? "chain's steps and inputs" : "function's instructions, inputs and output"} and drafts
            realistic cases with an expected output for each. Nothing is saved until you pick the ones you want. Generating and any images are
            billed to this account's credits as Tests usage.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField select size="small" label="Cases" value={count} onChange={e => setCount(Number(e.target.value))} sx={{ width: 110 }}>
              {Array.from(new Set([...COUNTS, count])).sort((a, b) => a - b).map(n => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox size="small" checked={avoidExisting} onChange={e => setAvoidExisting(e.target.checked)} />}
              label={`Don't repeat the ${existingCount} existing case${existingCount === 1 ? '' : 's'}`}
              disabled={!existingCount}
            />
            {takesImages && (
              <FormControlLabel
                control={<Checkbox size="small" checked={images} onChange={e => setImages(e.target.checked)} />}
                label="Make images for cases that need one"
              />
            )}
          </Stack>
          <TextField
            size="small"
            label="Guidance (optional)"
            placeholder="e.g. focus on non-English menus, or tricky portion sizes"
            value={guidance}
            onChange={e => setGuidance(e.target.value.slice(0, 2000))}
            multiline
            minRows={1}
            maxRows={4}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {busy === 'generating' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="caption" color="text.secondary" component="div">
                Writing cases… this takes up to a minute.
              </Typography>
            </Box>
          )}
          {!!drafts.length && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {selected.size} of {drafts.length} selected
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setSelected(new Set(drafts.map((_, i) => i)))}>
                    All
                  </Button>
                  <Button size="small" onClick={() => setSelected(new Set())}>
                    None
                  </Button>
                </Stack>
              </Stack>
              <Stack spacing={1}>
                {drafts.map((d, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, opacity: selected.has(i) ? 1 : 0.6 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox size="small" checked={selected.has(i)} onChange={() => toggle(i)} sx={{ mt: -0.5 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {d.name}
                          </Typography>
                          {d.critical && <Chip size="small" color="warning" variant="outlined" label="critical" />}
                          {d.tags.map(t => (
                            <Chip key={t} size="small" label={t} />
                          ))}
                        </Stack>
                        {d.description && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {d.description}
                          </Typography>
                        )}
                        <Box sx={{ my: 0.75 }}>
                          <InputChips preview={preview(d.inputs)} max={4} />
                        </Box>
                        {d.inputs
                          .filter(x => x.media)
                          .map(x => {
                            const made = !!x.media?.prompt && takesImages && images;
                            return (
                              <Typography key={x.field} variant="caption" color={made ? 'text.secondary' : 'warning.main'} component="div">
                                {made ? (
                                  <Tooltip title={x.media?.prompt || ''}>
                                    <span>An image is made for "{x.field}" when you save.</span>
                                  </Tooltip>
                                ) : (
                                  `"${x.field}" needs a ${x.media?.kind}: the case is saved switched off until you attach one.`
                                )}
                              </Typography>
                            );
                          })}
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            Expected:{' '}
                          </Box>
                          {expectedSummary(d.expected)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
              {!!problems.length && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  Left out: {problems.join('; ')}
                </Typography>
              )}
            </Box>
          )}
          {last && (
            <Typography variant="caption" color="text.secondary">
              {gen ? (
                <>
                  Written by{' '}
                  <Link component={RouterLink} to={`/dashboard/function-runs/${gen.function_id}?tab=performance&customer_id=${encodeURIComponent(gen.account_id)}`}>
                    {gen.name}
                  </Link>{' '}
                  ({modelLabel(last.model)}) in {gen.account_id}
                </>
              ) : (
                <>Written by {modelLabel(last.model)}</>
              )}{' '}
              · {fmtCredits(credits)} credits so far
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button startIcon={<GenerateIcon />} onClick={generate} disabled={!!busy}>
          {drafts.length ? `Generate ${count} more` : 'Generate'}
        </Button>
        <Button variant="contained" onClick={save} disabled={!!busy || !chosen.length}>
          {busy === 'saving'
            ? imagesToMake
              ? `Making ${imagesToMake} image${imagesToMake === 1 ? '' : 's'}…`
              : 'Saving…'
            : `Save ${chosen.length || ''} case${chosen.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
